import React from "react";
import styled from "styled-components";
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from "@mui/material";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { FormConfiguration } from "../FormConfiguration";

import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { FormRule } from "../Structure/FormRule";

// import { XFormRuleEditDialog } from "./XFormRuleEditDialog";
import { ExpressionConfiguration } from "../../rule-engine-core/Expressions/ExpressionConfiguration";

import { XFormRuleRow } from "./XFormRuleRow";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

interface IFormRulePage {
    form: FormConfiguration;
}

export const XFormRulePage = ({ form }: IFormRulePage) => {
    const onClickAddRule = () => {
        let rules = form.rules;
        let newRule = new FormRule(new ExpressionConfiguration());
        rules.push(newRule);
        form.rules = rules;
        StateManager.propertyChanged(form, "rules");
    };

    const onClickRemoveButton = (index: number) => {
        let rules = form.rules;
        rules.splice(index, 1);
        form.rules = rules;
        StateManager.propertyChanged(form, "rules");
    };

    const onDragendRow = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const [removed] = form.rules.splice(result.source.index, 1);
        form.rules.splice(result.destination.index, 0, removed);
        StateManager.propertyChanged(form, "rules");
    };

    return (
        <FormRulePageRender
            className="XFormRulePage-root"
            data-testid="XFormRulePage-root"
        >
            <ObserveState
                listenTo={form}
                properties={["rules"]}
                control={() => (
                    <>
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            mb={2}
                        >
                            <Button
                                sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                size="small"
                                variant="contained"
                                className="XFormAddRule-root"
                                onClick={onClickAddRule}
                            >
                                Add Rule
                            </Button>
                        </Box>
                        <StyledTableContainer
                            className="XFormRulePage-table"
                            data-testid="XFormRulePage-table"
                        >
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={true}
                                                direction={"asc"}
                                            >
                                                Rule Type
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell style={{ width: "150px" }}>
                                            <TableSortLabel
                                                active={false}
                                                direction={"asc"}
                                            >
                                                Run at form show
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell style={{ width: "360px" }}>
                                            <span>Expression</span>
                                        </TableCell>
                                        <TableCell>
                                            <span>Other Parameters</span>
                                        </TableCell>
                                        <TableCell style={{ width: "56px" }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                {form.rules.length > 0 && (
                                    <DragDropContext onDragEnd={onDragendRow}>
                                        <Droppable droppableId="ruleRowDrop">
                                            {(provided) => (
                                                <TableBody
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                >
                                                    {form.rules.map((rule, idx) => (
                                                        <XFormRuleRow
                                                            form={form}
                                                            rule={rule}
                                                            key={idx}
                                                            index={idx}
                                                            onAction={() => onClickRemoveButton(idx)}
                                                        />
                                                    ))}
                                                </TableBody>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                                {form.rules.length === 0 && (
                                    <TableBody>
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell colSpan={4}>You have no rules.</TableCell>
                                        </TableRow>
                                    </TableBody>
                                )}
                            </Table>
                        </StyledTableContainer>
                    </>
                )}
            />
        </FormRulePageRender>
    );
};

const FormRulePageRender = styled.div`
    height: 100%;
    width: 100%;

    position: relative;

    background: ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};

    padding-top: 24px;
    padding-left: 16px;
    padding-right: 16px;
`;

const StyledTableContainer = styled(TableContainer)`
    background: ${UIStyleConstants.WHITE_COLOR};
    border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
    box-sizing: border-box;
    border-radius: 4px;
    overflow: visible;

    .MuiTableCell-body {
        padding: 8px 0;
    }

    .MuiTableCell-head {
        padding: 12px 0;

        &,
        & .MuiTableSortLabel-root {
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 17px;
            letter-spacing: 0px;
            text-align: left;
            color: ${UIStyleConstants.GRAY_LIGHT_COLOR};
        }

        span {
            display: flex;
            width: 100%;
            padding: 3px 16px;
            border-left: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        }

        &:first-child {
            span {
                border: none;
            }
        }
    }
`;
